import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * * Hook that uses ResizeObserver to detect if an element's content is overflowing.
 * Returns a ref to attach to an element and a boolean indicating whether
 * the element's content is currently clipped (scrollWidth > clientWidth).
 *
 * Usage:
 *   const [ref, isOverflowing] = useIsOverflowing<HTMLSpanElement>();
 *   <Tooltip disableHoverListener={!isOverflowing}>
 *     <span ref={ref}>...</span>
 *   </Tooltip>
 */
function useIsOverflowing<T extends HTMLElement>(): [
  (node: T | null) => void,
  boolean
] {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const nodeRef = useRef<T | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const frameRef = useRef<number | null>(null);

  const check = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;

    const next = node.scrollWidth > node.clientWidth;
    setIsOverflowing((previous) => (previous === next ? previous : next));
  }, []);

  const scheduleCheck = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
    }

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      check();
    });
  }, [check]);

  const ref = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      nodeRef.current = node;

      if (!node) return;

      // Defer measurement so React updates do not happen during the browser's
      // ResizeObserver delivery cycle.
      scheduleCheck();
      observerRef.current = new ResizeObserver(scheduleCheck);
      observerRef.current.observe(node);
    },
    [scheduleCheck]
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();

      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    },
    []
  );

  return [ref, isOverflowing];
}

export default useIsOverflowing;
